<template>
  <div class="v-code-step">
    <div class="v-code-step__header">
      <span class="v-code-step__header__title">
        {{ title }}
      </span>
      <span class="v-code-step__header__subtitle">
        {{ subtitle }}
      </span>
    </div>

    <eewc-verification-code
      :value="value"
      :code-length="4"
      :error-message="errorMessage"
      @input="emit('input', $event)"
    />

    <div class="v-code-step__footer">
      <eewc-button-common
        class="v-code-step__footer__button"
        type="depressed"
        color="secondaryLight"
        :icon-size="20"
        icon-color="primary"
        icon
        disabled
        append-icon="$icon_clock"
      />
      <div class="v-code-step__footer__text">
        {{ t("It may take a minute to receive a code. Haven't received it?") }}
        <span
          class="v-code-step__footer__text--link"
          :class="{ 'v-code-step__footer__text--disabled': isCounterEnable }"
          :disabled="isCounterEnable"
          data-testid="my-profile-verification-wizard-step-resend-code"
          @click="resendCode"
        >
          {{ t('Resend code') }}
        </span>
      </div>
    </div>

    <div
      v-if="isCounterEnable"
      class="v-code-step__counter"
    >
      {{ t('You will be able to resend code again in') }}
      <span class="v-code-step__counter__number">{{ counter }} </span>
      {{ t('seconds') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { t } from '@/plugins/i18n.ts';
import { ref, computed } from 'vue';

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle: string;
    value: string;
    errorMessage?: string;
    counterTime?: number;
  }>(),
  {
    counterTime: 30,
    errorMessage: '',
  }
);

const emit = defineEmits<{
  (event: 'input', value: string): void;
  (event: 'resend-code'): void;
}>();

const counter = ref(0);
const isCounterEnable = computed(() => counter.value > 0);

function resendCode() {
  if (isCounterEnable.value) return;
  emit('resend-code');
  counter.value = props.counterTime;
  countDownTimer();
}

function countDownTimer() {
  if (counter.value <= 0) return;
  setTimeout(() => {
    counter.value -= 1;
    countDownTimer();
  }, 1000);
}
</script>

<style lang="scss" scoped>
@import '../styles/public/main.scss';

.v-code-step {
  &__header {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 32px;
    &__title {
      @include subtitle-1;
    }
    &__subtitle {
      @include body-2;
      color: $secondary;
      max-width: 480px;
    }
  }
  &__footer {
    margin-top: 32px;
    display: flex;
    align-items: center;
    gap: 12px;
    &__button {
      min-width: 36px;
      min-height: 36px;
    }
    &__text {
      @include body-2;
      color: $primary;
      max-width: 280px;
      &--link {
        @include subtitle-2;
        color: $accentDark;
        cursor: pointer;
      }
      &--disabled {
        color: $secondary;
        cursor: default;
      }
    }
  }
  &__counter {
    @include body-2;
    margin-top: 8px;
    color: $secondary;
    &__number {
      @include subtitle-2;
    }
  }
}
</style>
